// popover behaviour — backs the iconCss icon-picker grid.
//
// cleanSlate has no framework popover (bootstrap5 uses bootstrap.Popover), so each
// per-trigger instance is managed here. iconCss.js calls create(button, { content,
// trigger:'click', placement:'bottom' }), subscribes 'shown' (to wire the grid's
// icon clicks via the trigger's aria-describedby), and hide()s on select.

// Sequence counter for unique popover element ids (the trigger's aria-describedby
// points at the active popover, which iconCss.js reads to find the rendered grid).
let popoverSeq = 0;

// Build a popover instance bound to a trigger element: build content on each open
// (opts.content may be a function), append to <body>, fixed-position under the
// trigger (escapes any ancestor overflow:hidden), set the trigger's aria-describedby
// to the panel id, and fire the neutral shown/hidden events.
function createPopoverInstance(el, opts) {
    const options = opts || {};
    const handlers = { shown: [], hidden: [] };
    let panelEl = null;
    let isOpen = false;

    const fire = function (name) {
        handlers[name].forEach((handler) => handler(el));
    };

    // placement 'bottom' (the only placement iconCss requests): under the trigger.
    const position = function () {
        const rect = el.getBoundingClientRect();
        panelEl.style.position = 'fixed';
        panelEl.style.top = (rect.bottom + 6) + 'px';
        panelEl.style.left = rect.left + 'px';
    };

    const onDocPointer = function (e) {
        if (panelEl && !panelEl.contains(e.target) && !el.contains(e.target)) {
            instance.hide();
        }
    };

    // Close on page scroll/resize (the fixed panel would detach), but ignore scrolls
    // inside the panel itself (e.g. a long icon grid scrolling).
    const onReposition = function (e) {
        if (e && e.target && e.target.nodeType === 1 && panelEl && panelEl.contains(e.target)) {
            return;
        }
        instance.hide();
    };

    const instance = {
        show: function () {
            if (isOpen) {
                return;
            }
            isOpen = true;

            panelEl = document.createElement('div');
            panelEl.id = 'zit-cs-popover-' + (++popoverSeq);
            panelEl.className = 'zit-cs-popover';
            // content may be a string or a function returning HTML. html:true +
            // sanitize:false in the contract — iconCss builds the grid from internal
            // app data only (no user input), so injecting it as HTML is safe.
            const content = typeof options.content === 'function' ? options.content() : options.content;
            panelEl.innerHTML = content;

            document.body.appendChild(panelEl);
            // aria-describedby is the contract hook iconCss.js uses to locate the panel.
            el.setAttribute('aria-describedby', panelEl.id);
            position();

            setTimeout(() => {
                document.addEventListener('pointerdown', onDocPointer, true);
                window.addEventListener('scroll', onReposition, true);
                window.addEventListener('resize', onReposition, true);
            }, 0);

            fire('shown');
        },
        hide: function () {
            if (!isOpen) {
                return;
            }
            isOpen = false;

            document.removeEventListener('pointerdown', onDocPointer, true);
            window.removeEventListener('scroll', onReposition, true);
            window.removeEventListener('resize', onReposition, true);

            if (panelEl !== null) {
                panelEl.remove();
                panelEl = null;
            }
            el.removeAttribute('aria-describedby');

            fire('hidden');
        },
        dispose: function () {
            instance.hide();
            el.removeEventListener('click', onTriggerClick);
        },
        on: function (name, handler) {
            if (typeof handlers[name] === typeof undefined) {
                throw new Error('zit popover.on: unknown event "' + name + '"');
            }
            handlers[name].push(handler);
        }
    };

    // Toggle on trigger click (the only trigger iconCss uses; default to click).
    function onTriggerClick(e) {
        e.preventDefault();
        if (isOpen) {
            instance.hide();
        } else {
            instance.show();
        }
    }

    el.addEventListener('click', onTriggerClick);

    return instance;
}

const popover = {
    create: function (el, opts) {
        return createPopoverInstance(el, opts);
    }
};

export default popover;
